/**
 * Genererte Dokumenter - Håndter utkast og sendte dokumenter
 * Brukes for å lime inn dokumenter fra Claude Code
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './GenererteDokumenter.css';

export default function GenererteDokumenter({ sakId }) {
  const [dokumenter, setDokumenter] = useState([]);
  const [dokumenttyper, setDokumenttyper] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visNyDokumentModal, setVisNyDokumentModal] = useState(false);
  const [valgDokument, setValgDokument] = useState(null);
  const [visDokumentModal, setVisDokumentModal] = useState(false);

  // Form for nytt dokument
  const [nyttDokument, setNyttDokument] = useState({
    tittel: '',
    dokumenttype: '',
    html_innhold: ''
  });

  useEffect(() => {
    if (sakId) {
      lastDokumenter();
      lastDokumenttyper();
    }
  }, [sakId]);

  const lastDokumenter = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/saker/${sakId}/dokumenter`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Kunne ikke hente dokumenter');

      const data = await response.json();
      setDokumenter(data.dokumenter || []);
    } catch (error) {
      console.error('Feil ved lasting av dokumenter:', error);
      alert('Kunne ikke laste dokumenter: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const lastDokumenttyper = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/dokumenttyper`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDokumenttyper(data.dokumenttyper || []);
      }
    } catch (error) {
      console.error('Feil ved lasting av dokumenttyper:', error);
    }
  };

  const opprettDokument = async () => {
    if (!nyttDokument.tittel || !nyttDokument.dokumenttype || !nyttDokument.html_innhold) {
      alert('Vennligst fyll ut alle feltene');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/saker/${sakId}/dokumenter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(nyttDokument)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kunne ikke opprette dokument');
      }

      alert('Dokument lagret som utkast!');
      setVisNyDokumentModal(false);
      setNyttDokument({ tittel: '', dokumenttype: '', html_innhold: '' });
      lastDokumenter();
    } catch (error) {
      console.error('Feil ved opprettelse:', error);
      alert('Feil: ' + error.message);
    }
  };

  const markerSomSendt = async (dokumentId) => {
    const mottaker = prompt('Hvem ble dokumentet sendt til? (valgfritt)', '');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/dokumenter/${dokumentId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'sent',
          mottaker: mottaker || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kunne ikke oppdatere dokument');
      }

      alert('Dokument markert som sendt og lagt til i tidslinje!');
      lastDokumenter();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Feil: ' + error.message);
    }
  };

  const slettDokument = async (dokumentId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette utkastet?')) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/dokumenter/${dokumentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Kunne ikke slette dokument');
      }

      alert('Dokument slettet');
      lastDokumenter();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Feil: ' + error.message);
    }
  };

  const visDokument = async (dokumentId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/dokumenter/${dokumentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Kunne ikke hente dokument');

      const data = await response.json();
      setValgDokument(data.dokument);
      setVisDokumentModal(true);
    } catch (error) {
      console.error('Feil:', error);
      alert('Kunne ikke laste dokument: ' + error.message);
    }
  };

  const utkast = dokumenter.filter(d => d.status === 'draft');
  const sendte = dokumenter.filter(d => d.status === 'sent');

  if (loading) {
    return <div className="genererte-dokumenter-loading">Laster dokumenter...</div>;
  }

  return (
    <div className="genererte-dokumenter">
      <div className="dokumenter-header">
        <h3>📄 Dokumenter</h3>
        <button className="btn-ny-dokument" onClick={() => setVisNyDokumentModal(true)}>
          + Legg til dokument
        </button>
      </div>

      {/* UTKAST */}
      <div className="dokumenter-seksjon">
        <h4>✏️ Utkast ({utkast.length})</h4>
        {utkast.length === 0 ? (
          <p className="ingen-dokumenter">Ingen utkast</p>
        ) : (
          <div className="dokumenter-liste">
            {utkast.map(dok => (
              <div key={dok.id} className="dokument-kort utkast">
                <div className="dokument-info">
                  <strong>{dok.tittel}</strong>
                  <span className="dokument-type">{dok.dokumenttype}</span>
                  <span className="dokument-dato">
                    {new Date(dok.opprettet_dato).toLocaleDateString('nb-NO')}
                  </span>
                </div>
                <div className="dokument-handlinger">
                  <button onClick={() => visDokument(dok.id)} className="btn-vis">
                    👁️ Vis
                  </button>
                  <button onClick={() => markerSomSendt(dok.id)} className="btn-send">
                    ✅ Marker som sendt
                  </button>
                  <button onClick={() => slettDokument(dok.id)} className="btn-slett">
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SENDTE */}
      <div className="dokumenter-seksjon">
        <h4>✉️ Sendte dokumenter ({sendte.length})</h4>
        {sendte.length === 0 ? (
          <p className="ingen-dokumenter">Ingen sendte dokumenter</p>
        ) : (
          <div className="dokumenter-liste">
            {sendte.map(dok => (
              <div key={dok.id} className="dokument-kort sendt">
                <div className="dokument-info">
                  <strong>{dok.tittel}</strong>
                  <span className="dokument-type">{dok.dokumenttype}</span>
                  <span className="dokument-dato">
                    Sendt: {new Date(dok.sendt_dato).toLocaleDateString('nb-NO')}
                  </span>
                  {dok.mottaker && <span className="dokument-mottaker">Til: {dok.mottaker}</span>}
                </div>
                <div className="dokument-handlinger">
                  <button onClick={() => visDokument(dok.id)} className="btn-vis">
                    👁️ Vis
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL: Nytt dokument */}
      {visNyDokumentModal && (
        <div className="modal-overlay" onClick={() => setVisNyDokumentModal(false)}>
          <div className="modal-innhold" onClick={e => e.stopPropagation()}>
            <h3>📝 Legg til nytt dokument</h3>

            <div className="form-gruppe">
              <label>Tittel *</label>
              <input
                type="text"
                value={nyttDokument.tittel}
                onChange={e => setNyttDokument({...nyttDokument, tittel: e.target.value})}
                placeholder="F.eks: Juridisk vurdering - Kontraktsbrudd"
              />
            </div>

            <div className="form-gruppe">
              <label>Dokumenttype *</label>
              <select
                value={nyttDokument.dokumenttype}
                onChange={e => setNyttDokument({...nyttDokument, dokumenttype: e.target.value})}
              >
                <option value="">-- Velg type --</option>
                {dokumenttyper.map(type => (
                  <option key={type.kode} value={type.kode}>
                    {type.navn}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-gruppe">
              <label>HTML-innhold (fra Claude Code) *</label>
              <textarea
                value={nyttDokument.html_innhold}
                onChange={e => setNyttDokument({...nyttDokument, html_innhold: e.target.value})}
                placeholder="Lim inn HTML-koden fra Claude Code her..."
                rows={15}
              />
              <small>Tip: Kopier hele HTML-filen fra Claude Code og lim inn her</small>
            </div>

            <div className="modal-knapper">
              <button onClick={opprettDokument} className="btn-lagre">
                💾 Lagre som utkast
              </button>
              <button onClick={() => setVisNyDokumentModal(false)} className="btn-avbryt">
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Vis dokument */}
      {visDokumentModal && valgDokument && (
        <div className="modal-overlay" onClick={() => setVisDokumentModal(false)}>
          <div className="modal-innhold modal-dokument" onClick={e => e.stopPropagation()}>
            <div className="dokument-header">
              <h3>{valgDokument.tittel}</h3>
              <button onClick={() => setVisDokumentModal(false)} className="btn-lukk">✕</button>
            </div>
            <div className="dokument-metadata">
              <span><strong>Type:</strong> {valgDokument.dokumenttype}</span>
              <span><strong>Status:</strong> {valgDokument.status === 'draft' ? 'Utkast' : 'Sendt'}</span>
              <span><strong>Opprettet:</strong> {new Date(valgDokument.opprettet_dato).toLocaleDateString('nb-NO')}</span>
            </div>
            <div className="dokument-innhold" dangerouslySetInnerHTML={{ __html: valgDokument.html_innhold }} />
          </div>
        </div>
      )}
    </div>
  );
}
